import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { MenuItem } from '@openng/optimus-ui/api';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { DatabaseUser } from 'src/modules/database/shared/database-user.entity';
import { NetworkLinkedDocument } from 'src/modules/enaio/netgen/NetworkLinkedDocument';
import { NetworkLinkedDocumentCategory } from 'src/modules/enaio/netgen/NetworkLinkedDocumentCategory';
import { NetworkLinkedDocumentShareType } from 'src/modules/enaio/netgen/NetworkLinkedDocumentShareType';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { TableCellButton } from 'src/modules/sm-base/models/table-cell-button.model';
import { TableCellEvent } from 'src/modules/sm-base/models/table-cell-event.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { Objects } from 'src/modules/utils/shared/objects';
import { Utils } from 'src/modules/utils/shared/utils';
import { ProjectPlanningService } from '../../services/project-planning.service';
import { EditNetworkLinkedDocumentComponent } from '../edit-network-linked-document/edit-network-linked-document.component';

@Component({
    selector: 'app-planning-view-documents',
    templateUrl: './planning-view-documents.component.html',
    styleUrl: './planning-view-documents.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PlanningViewDocumentsComponent extends ComponentView {

    service = inject(ProjectPlanningService);

    data: NetworkLinkedDocument[] = [];
    users: DatabaseUser[] = [];
    categories: NetworkLinkedDocumentCategory[] = [];
    selectedDocument: NetworkLinkedDocument = null;
    table: TableData;
    pdfSrc: Blob | string = "";
    
    async initParams(): Promise<boolean> {
        this.categories = await this.service.getNetworkLinkedDocumentCategories(true);
        this.data = await this.service.getNetworkLinkedDocuments(true);
        this.users = await this.service.getActiveUsers();
        this.fillTable();
        return true;
    }

    async update(): Promise<void> {
        this.categories = await this.service.getNetworkLinkedDocumentCategories(true);
        this.data = await this.service.getNetworkLinkedDocuments(true);
        this.fillTable();       
    }

    async addDocument(): Promise<void> {
        await this.app.dialog.component(EditNetworkLinkedDocumentComponent, this.app.t("ProjectPlanning.newDocument"), null, {
            id: 0
        });  
        await this.update();
    }

    fillTable(): void {
        this.table = new TableData([
            new TableColumn("actions", "", TableCellType.button, 1),
            new TableColumn("category", this.app.t("entity.NetworkLinkedDocumentCategory.entityName"), TableCellType.string, 20),
            new TableColumn("title", this.app.t("entity.NetworkLinkedDocument.fields.title"), TableCellType.string, 40),
            new TableColumn("createdBy", this.app.t("ProjectPlanning.creator"), TableCellType.string, 20),
            new TableColumn("shareType", this.app.t("entity.NetworkLinkedDocument.fields.shareType"), TableCellType.string, 20)           
        ], this.data.map(doc => new TableRow(Objects.toString(doc.id), doc, {
            actions: [doc.canDownload() ? 
                new TableCellButton("download", "fas fa-download", this.app.t("ProjectPlanning.downloadFile")) :
                new TableCellButton("clipboard", "fas fa-clipboard", this.app.t("ProjectPlanning.copyFilePath"))
            ],
            title: doc.title,
            category: doc.category?.title ?? this.categories.find(c => c.id == doc.categoryId)?.title ?? "",
            createdBy: this.users.find(e => e.id == doc.createdById)?.getFullName() ?? "<???>",
            shareType : NetworkLinkedDocumentShareType.getDisplayName(doc.shareType, this.app)
        }, { 
            getContextMenuItems: this.getContextMenuItems.bind(this)
        })), ["category", "title"]);
    }

    async rowSelect(event): Promise<void> {
        this.selectedDocument = event?.raw;
        if (this.selectedDocument == null) {
            this.pdfSrc = "";
        }
        else {
            this.pdfSrc = await RestEndpoint.main().query({ id: this.selectedDocument.id, getContents: true, pdf: true }).runBlob("api/app/networkLinkedDocument", true);
        }
    }

    async clickCell(event: TableCellEvent): Promise<void> {
        if (event.isButton("download", "actions")) {
            let doc = event.row.raw as NetworkLinkedDocument;
            await RestEndpoint.main().query({ id: doc.id, getContents: true }).runSaveAs("api/app/networkLinkedDocument", Utils.getFileNameWithoutPath(doc.filePath));
        }
        else if (event.isButton("clipboard", "actions")) {
            let doc = event.row.raw as NetworkLinkedDocument;
            await this.app.copyToClipboardOrDialog(doc.filePath, this.app.t("ProjectPlanning.copyFilePath"), this.app.t("ProjectPlanning.filePathCopied"));
        }
    }

    getContextMenuItems(e: TableCellEvent): MenuItem[] {
        return this.service.getNetworkLinkedDocumentContextMenuItems(e.row.raw as NetworkLinkedDocument, this.service.getCurrentUser(), this.update.bind(this), this);
    }

    async doubleClick(row: TableRow): Promise<void> {
        await this.app.dialog.component(EditNetworkLinkedDocumentComponent, this.app.t("ProjectPlanning.editDocument"), null, {
            id: (row.raw as NetworkLinkedDocument).id
        });  
        await this.update();
    }

}
